import React, { useCallback, useState, useEffect } from 'react';
import SwitchField from '../dynamic-form/switch-field';
import DynamicFieldList from '../dynamic-form/dynamic-field-list';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import IconButton from '@material-ui/core/IconButton';
import { useFormikContext, getIn } from 'formik';
import { useStations } from '../../hooks/stations';

const useStyles = makeStyles((theme) => ({
  accordion: {
    width: '100%',
  },
  description: {
    marginBottom: theme.spacing(3),
  },
  header: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

export const OutrageForm = () => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const { values, setFieldValue } = useFormikContext();
  const serviceName = getIn(values, 'serviceName');
  const uicStationCode = getIn(values, 'uicStationCode');
  const formDisabled = getIn(values, 'formDisabled');
  const outrage = getIn(values, 'fieldReportsDto.priorityLevelTwo.outrage');
  const { handleServiceNameChange, stationOptions } = useStations();
  const handleOnChange = useCallback(() => {
    if (outrage) {
      return setExpanded((expanded) => !expanded);
    }
  }, [outrage]);

  useEffect(() => {
    if (serviceName) {
      handleServiceNameChange(serviceName);
    }
  }, [serviceName, handleServiceNameChange]);

  useEffect(() => {
    if (uicStationCode && outrage) {
      const stationName = stationOptions.find((station) => station.value === uicStationCode);
      setFieldValue('fieldReportsDto.outrage.placeIcident', stationName?.label);
    }
  }, [uicStationCode, outrage, stationOptions, setFieldValue]);

  useEffect(() => {
    if (!outrage) {
      setFieldValue('fieldReportsDto.outrage.pvDate', new Date());
      setFieldValue('fieldReportsDto.outrage.placeIcident', null);
      setFieldValue('fieldReportsDto.outrage.statements', '');
      setFieldValue('fieldReportsDto.outrage.exactTerms', '');
      setFieldValue('fieldReportsDto.outrage.witnessFacts', '');
      setFieldValue('fieldReportsDto.outrage.pvReference', '');
    }
  }, [outrage, setFieldValue]);

  useEffect(() => {
    setExpanded(outrage);
  }, [outrage]);

  return (
    <Accordion className={classes.accordion} expanded={expanded}>
      <div className={classes.header}>
        <SwitchField
          label="PV pour outrage"
          name="fieldReportsDto.priorityLevelTwo.outrage"
          disabled={formDisabled}
        />
        {outrage && (
          <IconButton onClick={handleOnChange}>
            <ExpandMoreIcon />
          </IconButton>
        )}
      </div>
      <AccordionDetails>
        <div>
          <Typography className={classes.description} color="textSecondary">
            Cette partie est à compléter pour générer un PV d'outrage transmis à ton manager et au
            responsable de la sûreté lors de l'envoi du rapport.
          </Typography>
          <DynamicFieldList
            fields={{
              'fieldReportsDto.outrage.pvDate': {
                type: 'date',
                label: 'Date de saisie',
                disabled: formDisabled,
                grid: 6,
              },
              'fieldReportsDto.outrage.placeIcident': {
                type: 'text',
                label: "Lieu de l'incident",
                options: stationOptions,
                disabled: formDisabled,
                grid: 6,
              },
              'fieldReportsDto.outrage.statements': {
                type: 'text',
                label: "Déclarations factuelles de l'agent sur le déroulement des faits",
                multiline: true,
                disabled: formDisabled,
                rows: 4,
                rowsMax: 15,
              },
              'fieldReportsDto.outrage.exactTerms': {
                type: 'text',
                label: "Termes exacts de l'outrage ou description précise des gestes outrageants",
                multiline: true,
                disabled: formDisabled,
                rows: 4,
                rowsMax: 15,
              },
              'fieldReportsDto.outrage.witnessFacts': {
                type: 'text',
                label: 'Temoin(s) des faits (nom + prenom + qualité)',
                disabled: formDisabled,
                grid: 6,
              },
              'fieldReportsDto.outrage.pvReference': {
                type: 'text',
                label: 'Référence PV',
                disabled: formDisabled,
                grid: 6,
              },
            }}
          />
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
